import React, { useState } from 'react';
import './ContactPage.css';
import { useSEO } from '../lib/seo';

const TOPICS = [
  'Order & Delivery',
  'Plant Care Help',
  'Returns & Refunds',
  'Corporate Gifting',
  'Vertical Garden',
  'Press & Media',
  'Other',
];

const CHANNELS = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.59 3.47 2 2 0 0 1 3.56 1.3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.84a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
    label: 'Call us',
    value: '+91 76969 99018',
    sub: 'Mon – Sat, 9 am – 6 pm IST',
    href: 'tel:+917696999018',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    label: 'Email us',
    value: 'hello@naturiq.in',
    sub: 'We reply within a few hours',
    href: 'mailto:hello@naturiq.in',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    ),
    label: 'WhatsApp',
    value: 'Chat with us',
    sub: 'Usually responds in minutes',
    href: 'https://wa.me/917696999018',
  },
];

const FAQS = [
  {
    q: 'When will my plant arrive?',
    a: 'Most orders ship within 1–2 business days and arrive in 3–5 days. You\'ll get a tracking link once your order is dispatched.',
  },
  {
    q: 'What if my plant arrives damaged?',
    a: 'We offer a 14-day arrival guarantee. Send us a photo within 14 days of delivery and we\'ll send a replacement or issue a refund — your choice.',
  },
  {
    q: 'Do you ship across India?',
    a: 'Yes, we deliver to 100+ cities. Enter your pincode at checkout to confirm availability for your location.',
  },
  {
    q: 'Can I change or cancel my order?',
    a: 'Orders can be modified or cancelled within 2 hours of placing them. After that, they enter our packing queue. Contact us quickly and we\'ll do our best.',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`contact-faq__item${open ? ' contact-faq__item--open' : ''}`}>
      <button className="contact-faq__q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <svg className="contact-faq__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && <p className="contact-faq__a">{a}</p>}
    </div>
  );
}

// Strip HTML tags before storing/sending — prevents XSS in admin views
const sanitize = (val, maxLen) =>
  (typeof val === 'string' ? val : '').replace(/<[^>]*>/g, '').slice(0, maxLen);

const VALID_TOPICS = new Set([
  'Order & Delivery', 'Plant Care Help', 'Returns & Refunds',
  'Corporate Gifting', 'Vertical Garden', 'Press & Media', 'Other',
]);

const VALIDATORS = {
  name: v => {
    const t = v.trim();
    if (!t) return 'Name is required.';
    if (t.length < 2) return 'Name must be at least 2 characters.';
    if (t.length > 100) return 'Name is too long.';
    if (!/^[a-zA-Z\s'\-]+$/.test(t)) return 'Name can only contain letters, spaces, hyphens, or apostrophes.';
    return '';
  },
  email: v => {
    const t = v.trim();
    if (!t) return 'Email address is required.';
    if (t.length > 254) return 'Email address is too long.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return 'Please enter a valid email address.';
    return '';
  },
  mobile: v => {
    const digits = v.replace(/\D/g, '');
    if (!v.trim()) return 'Mobile number is required.';
    if (digits.length < 10) return 'Mobile number must be at least 10 digits.';
    if (digits.length > 13) return 'Mobile number is too long.';
    if (!/^[+\d\s\-()[\]]+$/.test(v.trim())) return 'Please enter a valid mobile number.';
    return '';
  },
  topic: v => {
    if (!v) return 'Please select a topic.';
    if (!VALID_TOPICS.has(v)) return 'Please select a valid topic.';
    return '';
  },
  message: v => {
    const t = v.trim();
    if (!t) return 'Message is required.';
    if (t.length < 10) return 'Message must be at least 10 characters.';
    if (t.length > 1000) return 'Message must be under 1000 characters.';
    return '';
  },
};

export default function ContactPage() {
  useSEO({
    title: 'Contact Us — Get in Touch',
    description: "Have a question? Contact Naturiq via WhatsApp, email or our contact form. We're here to help with plant care, orders and deliveries.",
    url: '/contact',
  });

  const [form, setForm] = useState({ name: '', email: '', mobile: '', topic: '', message: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const validate = (fields = form) => {
    const errs = {};
    Object.keys(VALIDATORS).forEach(k => {
      const msg = VALIDATORS[k](fields[k]);
      if (msg) errs[k] = msg;
    });
    return errs;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    setForm(updated);
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: VALIDATORS[name](value) }));
    }
  };

  const handleBlur = e => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: VALIDATORS[name](value) }));
  };

  const handleSubmit = async evt => {
    evt.preventDefault();
    if (status === 'sending') return; // prevent double-submit
    const allTouched = Object.keys(VALIDATORS).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);
    const errs = validate();
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;
    setStatus('sending');
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Sanitize before sending — strip any HTML tags a user might inject
      const payload = {
        source: 'contact',
        name:    sanitize(form.name,    100),
        email:   sanitize(form.email,   254).toLowerCase(),
        mobile:  sanitize(form.mobile,   20),
        topic:   VALID_TOPICS.has(form.topic) ? form.topic : '',
        message: sanitize(form.message, 1000),
      };
      const res = await fetch(`${BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let errMsg = 'Something went wrong. Please try again or reach us directly.';
        try { const d = await res.json(); if (d?.error) errMsg = d.error; } catch (_) {}
        console.error('Contact form server error:', errMsg);
        setStatus('error');
        return;
      }
      setStatus('sent');
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    }
  };

  const fieldClass = name =>
    `contact-form__input${touched[name] && errors[name] ? ' contact-form__input--error' : touched[name] && !errors[name] ? ' contact-form__input--valid' : ''}`;

  return (
    <main className="contact-page">

      {/* ── Hero ── */}
      <section className="contact-hero">
        <div className="contact-hero__content">
          <p className="contact-hero__eyebrow">Contact us</p>
          <h1 className="contact-hero__title">We're real people.<br />We actually pick up.</h1>
          <p className="contact-hero__sub">
            Whether it's a wilting Monstera or a missing order — reach out and
            someone who actually knows plants will get back to you.
          </p>
        </div>
        <div className="contact-hero__img-wrap">
          <img
            src="https://cdn.mos.cms.futurecdn.net/pft2PFLcznxvyQ2CWNHQCG.jpg"
            alt="Naturiq team member answering customer queries"
            className="contact-hero__img"
          />
        </div>
      </section>

      {/* ── Channels ── */}
      <section className="contact-channels">
        {CHANNELS.map(ch => (
          <a key={ch.label} href={ch.href} className="contact-channel-card" target={ch.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
            <div className="contact-channel-card__icon">{ch.icon}</div>
            <div className="contact-channel-card__body">
              <p className="contact-channel-card__label">{ch.label}</p>
              <p className="contact-channel-card__value">{ch.value}</p>
              <p className="contact-channel-card__sub">{ch.sub}</p>
            </div>
            <svg className="contact-channel-card__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
            </svg>
          </a>
        ))}
      </section>

      {/* ── Form + FAQs ── */}
      <section className="contact-body">

        {/* Form */}
        <div className="contact-form-wrap">
          <p className="contact-section__eyebrow">Send a message</p>
          <h2 className="contact-section__title">Tell us what's on your mind</h2>
          <p className="contact-section__sub">We'll get back to you within a few hours on weekdays.</p>

          {status === 'error' && (
            <div className="contact-form__error-banner">
              Something went wrong. Please try again or reach us directly via phone or email.
            </div>
          )}
          {status === 'sent' ? (
            <div className="contact-form__success">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M8 12.5l2.5 2.5 5-5"/>
              </svg>
              <h3>Message received!</h3>
              <p>We'll be in touch soon. In the meantime, check our FAQs below — your answer might already be there.</p>
              <button className="contact-form__reset" onClick={() => { setForm({ name: '', email: '', mobile: '', topic: '', message: '' }); setErrors({}); setTouched({}); setStatus('idle'); }}>
                Send another message
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              {/* Honeypot — hidden from real users, bots fill it in */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ display: 'none' }}
                onChange={() => {}}
              />
              <div className="contact-form__row">
                <div className="contact-form__field">
                  <label className="contact-form__label" htmlFor="cf-name">Your name</label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    className={fieldClass('name')}
                    placeholder="Priya Sharma"
                    maxLength={100}
                    value={form.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.name && errors.name && <span className="contact-form__error">{errors.name}</span>}
                  {touched.name && !errors.name && <span className="contact-form__valid">Looks good!</span>}
                </div>
                <div className="contact-form__field">
                  <label className="contact-form__label" htmlFor="cf-email">Email address</label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    className={fieldClass('email')}
                    placeholder="priya@example.com"
                    maxLength={254}
                    value={form.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {touched.email && errors.email && <span className="contact-form__error">{errors.email}</span>}
                  {touched.email && !errors.email && <span className="contact-form__valid">Looks good!</span>}
                </div>
              </div>
              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="cf-mobile">Mobile number</label>
                <input
                  id="cf-mobile"
                  name="mobile"
                  type="tel"
                  className={fieldClass('mobile')}
                  placeholder="+91 76969 99018"
                  maxLength={20}
                  value={form.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.mobile && errors.mobile && <span className="contact-form__error">{errors.mobile}</span>}
                {touched.mobile && !errors.mobile && <span className="contact-form__valid">Looks good!</span>}
              </div>
              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="cf-topic">What's it about?</label>
                <select
                  id="cf-topic"
                  name="topic"
                  className={`contact-form__select${touched.topic && errors.topic ? ' contact-form__input--error' : touched.topic && !errors.topic ? ' contact-form__input--valid' : ''}`}
                  value={form.topic}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select a topic…</option>
                  {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {touched.topic && errors.topic && <span className="contact-form__error">{errors.topic}</span>}
              </div>
              <div className="contact-form__field">
                <label className="contact-form__label" htmlFor="cf-message">
                  Message
                  <span className="contact-form__char-count">{form.message.length}/1000</span>
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  className={`contact-form__textarea${touched.message && errors.message ? ' contact-form__input--error' : touched.message && !errors.message ? ' contact-form__input--valid' : ''}`}
                  placeholder="My order number is #NQ1234 and…"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={1000}
                />
                {touched.message && errors.message && <span className="contact-form__error">{errors.message}</span>}
              </div>
              <button
                type="submit"
                className={`contact-form__submit${status === 'sending' ? ' contact-form__submit--loading' : ''}`}
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>

        {/* FAQs */}
        <div className="contact-faq-wrap">
          <p className="contact-section__eyebrow">Quick answers</p>
          <h2 className="contact-section__title">Common questions</h2>
          <p className="contact-section__sub">The most frequent things people ask us.</p>
          <div className="contact-faq">
            {FAQS.map(f => <FaqItem key={f.q} q={f.q} a={f.a} />)}
          </div>

          <div className="contact-office">
            <p className="contact-office__label">Our office</p>
            <p className="contact-office__address">
              Naturiq India<br />
              289 Theur, Tarmala Road<br />
              Pune, Maharashtra 412201 India
            </p>
            <p className="contact-office__hours">Mon – Sat · 9 am – 6 pm IST</p>
          </div>

          <div className="contact-social">
            <span className="contact-social__label">Follow us</span>
            <div className="contact-social__icons">
              <a href="https://instagram.com/naturiq.in" className="contact-social__link" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
              </a>
              <a href="https://facebook.com/naturiq.in" className="contact-social__link" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              </a>
              <a href="https://linkedin.com/company/naturiq-in" className="contact-social__link" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
                </svg>
              </a>
              <a href="https://twitter.com/Naturiq_in" className="contact-social__link" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                <svg width="20" height="20" viewBox="0 0 1200 1227" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.163 519.284ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.828Z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

      </section>

    </main>
  );
}